import { useEffect, useRef, useState } from 'react';
import { Play, Pause, RotateCcw, Headphones, EyeOff } from 'lucide-react';

interface ListeningPlayerProps {
  /** Script à LIRE (jamais affiché) — comme un vrai enregistrement de listening. */
  text: string;
  /** Langue de la voix (BCP-47), ex. « en-US » pour TOEFL. */
  lang?: string;
  className?: string;
}

type Gender = 'm' | 'f' | 'n';
interface Segment { speaker: string | null; text: string; }

// Étiquettes de locuteur reconnues (dialogues type examen). L'étiquette est
// RETIRÉE du texte lu (on n'énonce jamais « Man » / « Woman »).
const SPEAKER_RE = /\b(Man|Woman|Boy|Girl|Student|Professor|Teacher|Advisor|Interviewer|Narrator|Speaker(?:\s*[A-Z0-9])?)\s*:\s*/g;

/** Découpe le script en segments par locuteur (sans les étiquettes). */
function parseSegments(text: string): Segment[] {
  const segs: Segment[] = [];
  let last = 0;
  let cur: string | null = null;
  let m: RegExpExecArray | null;
  SPEAKER_RE.lastIndex = 0;
  while ((m = SPEAKER_RE.exec(text)) !== null) {
    const chunk = text.slice(last, m.index).trim();
    if (chunk) segs.push({ speaker: cur, text: chunk });
    cur = m[1];
    last = m.index + m[0].length;
  }
  const tail = text.slice(last).trim();
  if (tail) segs.push({ speaker: cur, text: tail });
  return segs.length ? segs : [{ speaker: null, text: text.trim() }];
}

/**
 * ─── ListeningPlayer ─────────────────────────────────────────────────────────
 *
 * Transforme un transcript textuel en véritable exercice de compréhension ORALE :
 * le texte est MASQUÉ et lu à voix haute par la synthèse vocale du navigateur
 * (Web Speech API). Les DIALOGUES sont joués avec une voix/tonalité DIFFÉRENTE
 * par locuteur (homme/femme), et les étiquettes « Man: » / « Woman: » ne sont
 * jamais énoncées. 100 % navigateur — aucun serveur, aucun fichier audio.
 */
export function ListeningPlayer({ text, lang = 'en-US', className = '' }: ListeningPlayerProps) {
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const [revealed, setRevealed] = useState(false);
  const [, setVoicesTick] = useState(0);
  const segments = useRef<Segment[]>(parseSegments(text));

  useEffect(() => { segments.current = parseSegments(text); }, [text]);

  // Les voix se chargent parfois de façon asynchrone.
  useEffect(() => {
    if (!supported) return;
    const onVoices = () => setVoicesTick((t) => t + 1);
    window.speechSynthesis.addEventListener?.('voiceschanged', onVoices);
    return () => window.speechSynthesis.removeEventListener?.('voiceschanged', onVoices);
  }, [supported]);

  const stop = () => {
    if (supported) window.speechSynthesis.cancel();
    setPlaying(false);
  };
  useEffect(() => stop, [text, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Meilleures voix masculine/féminine disponibles pour la langue. */
  const pickGenderVoices = () => {
    const base = lang.split('-')[0].toLowerCase();
    const all = window.speechSynthesis.getVoices();
    const pool = all.filter((v) => v.lang.toLowerCase().startsWith(base));
    const list = pool.length ? pool : all;
    const female = list.find((v) => /female|zira|samantha|susan|karen|fiona|tessa|victoria|moira|serena|woman|aria|jenny/i.test(v.name)) || null;
    const male = list.find((v) => v !== female && /male|david|mark|daniel|alex|fred|rishi|george|guy|tom|man/i.test(v.name)) || null;
    // Repli : deux voix distinctes quelconques.
    const distinct = list.filter((v) => v !== female && v !== male);
    return {
      female: female || distinct[0] || list[0] || null,
      male: male || distinct.find((v) => v !== (female || distinct[0])) || list[1] || list[0] || null,
    };
  };

  const genderOf = (speaker: string | null, assign: Record<string, Gender>, altRef: { i: number }): Gender => {
    if (!speaker) return 'n';
    if (assign[speaker]) return assign[speaker];
    let g: Gender;
    if (/wom|girl|female|mrs|\bms\b|\bshe\b|\bher\b/i.test(speaker)) g = 'f';
    else if (/\bman\b|boy|male|\bmr\b|\bhe\b|\bhim\b/i.test(speaker)) g = 'm';
    else g = altRef.i++ % 2 === 0 ? 'm' : 'f'; // locuteur non genré → on alterne
    assign[speaker] = g;
    return g;
  };

  const speakAll = () => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    const { male, female } = pickGenderVoices();
    const assign: Record<string, Gender> = {};
    const altRef = { i: 0 };
    const segs = segments.current;

    segs.forEach((seg, idx) => {
      const g = genderOf(seg.speaker, assign, altRef);
      const u = new SpeechSynthesisUtterance(seg.text);
      u.lang = lang;
      u.rate = rate;
      // Voix par genre si dispo, ET tonalité (pitch) pour différencier même
      // quand le système ne propose qu'une seule voix.
      if (g === 'f') { if (female) u.voice = female; u.pitch = 1.15; }
      else if (g === 'm') { if (male) u.voice = male; u.pitch = 0.85; }
      else { if (male || female) u.voice = (male || female)!; u.pitch = 1; }
      if (idx === segs.length - 1) u.onend = () => setPlaying(false);
      u.onerror = () => setPlaying(false);
      window.speechSynthesis.speak(u);
    });
    setPlaying(true);
  };

  const toggle = () => {
    if (!supported) return;
    if (playing) {
      window.speechSynthesis.pause();
      setPlaying(false);
    } else if (window.speechSynthesis.paused && window.speechSynthesis.speaking) {
      window.speechSynthesis.resume();
      setPlaying(true);
    } else {
      speakAll();
    }
  };

  const changeRate = () => {
    const next = rate === 1 ? 0.75 : rate === 0.75 ? 1.25 : 1;
    setRate(next);
    if (playing) { window.speechSynthesis.cancel(); setTimeout(speakAll, 60); }
  };

  if (!supported) {
    return (
      <div className={`rounded-xl border border-line bg-surface p-3 ${className}`}>
        <p className="text-[11px] text-fg-subtle mb-1.5 flex items-center gap-1">
          <Headphones className="w-3.5 h-3.5" /> Audio non supporté — transcript :
        </p>
        <p className="text-sm text-fg-muted whitespace-pre-wrap">{text}</p>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border border-line bg-surface p-3 sm:p-4 ${className}`}>
      <div className="flex items-center gap-3">
        <button
          onClick={toggle}
          className="w-11 h-11 bg-green-500/15 rounded-full flex items-center justify-center flex-shrink-0 hover:bg-green-500/25 transition-colors"
          aria-label={playing ? 'Pause' : 'Écouter'}
        >
          {playing ? <Pause className="w-5 h-5 text-green-400" /> : <Play className="w-5 h-5 text-green-400 ml-0.5" />}
        </button>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-fg flex items-center gap-1.5">
            <Headphones className="w-4 h-4 text-green-400" /> Enregistrement audio
          </p>
          <p className="text-[11px] text-fg-subtle flex items-center gap-1">
            <EyeOff className="w-3 h-3" /> Le texte est masqué — écoutez, comme au vrai examen.
          </p>
        </div>

        <button onClick={speakAll} className="p-2 rounded-lg text-fg-subtle hover:text-fg hover:bg-overlay-2 transition-colors" title="Réécouter depuis le début">
          <RotateCcw className="w-4 h-4" />
        </button>
        <button onClick={changeRate} className="px-2 py-1 rounded-lg text-xs font-mono text-fg-subtle hover:text-fg hover:bg-overlay-2 transition-colors" title="Vitesse de lecture">
          {rate}×
        </button>
      </div>

      <button
        onClick={() => setRevealed((r) => !r)}
        className="mt-2 text-[11px] text-fg-faint hover:text-fg-subtle underline decoration-dotted"
      >
        {revealed ? 'Masquer le transcript' : 'Afficher le transcript'}
      </button>
      {revealed && <p className="mt-1.5 text-sm text-fg-muted whitespace-pre-wrap">{text}</p>}
    </div>
  );
}
